import { useEffect, useMemo, useRef, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { captureDomEvidence, type DomEvidence } from '@/lib/checkoutEvidence'
import { formatCurrency } from '@/lib/formatters'
import type { QuoteCardData } from './quoteCardTypes'
import { useQuoteCart } from './QuoteCartContext'
import { cartTotal, groupCartByQuote } from './quoteCartItems'
import { QuoteCartQuoteRow } from './QuoteCartQuoteRow'
import { CartItemScope, QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

interface QuoteCartSidebarProps {
  /** Every quote on the page, not the filtered list — the cart keeps what filters hide. */
  quotes: QuoteCardData[]
  /** Sends the cart to the broker. Receives one deduplicated indication id per carrier. */
  onCheckout: (indicationIds: string[], evidence: DomEvidence) => Promise<void> | void
  /** Why the cart already holds something, e.g. a quote the customer picked by email. */
  notice?: string | null
}

/** Right-side cart panel: what the customer has picked, grouped by carrier, and the way out to checkout. */
export function QuoteCartSidebar({ quotes, onCheckout, notice }: QuoteCartSidebarProps) {
  const { keys, indicationIds, isOpen, close, remove } = useQuoteCart()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const track = useTrackQuotesEvent()
  const wasOpen = useRef(false)

  const groups = useMemo(() => groupCartByQuote(keys, quotes), [keys, quotes])
  const isEmpty = groups.length === 0

  const cartProperties = {
    item_count: keys.size,
    carrier_count: groups.length,
    carrier_names: groups.map((group) => group.carrierName),
    cart_total: cartTotal(groups),
  }

  // The nav's cart button opens this too, so the panel reports its own state rather
  // than every opener having to.
  useEffect(() => {
    if (isOpen === wasOpen.current) return
    wasOpen.current = isOpen
    track(isOpen ? QuotesEvent.CART_OPENED : QuotesEvent.CART_CLOSED, cartProperties)
  })

  async function checkOut() {
    setHasFailed(false)
    setIsSubmitting(true)
    track(QuotesEvent.CHECKOUT_REQUESTED, { ...cartProperties, indication_count: indicationIds.length })
    try {
      // Captured before the request, while the cart the customer agreed to is still on screen.
      await onCheckout(indicationIds, await captureDomEvidence())
      track(QuotesEvent.CHECKOUT_SUCCEEDED, { ...cartProperties, indication_count: indicationIds.length })
    } catch {
      track(QuotesEvent.CHECKOUT_FAILED, { ...cartProperties, indication_count: indicationIds.length })
      setHasFailed(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (open) return
        setHasFailed(false)
        close()
      }}
    >
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-ink-100 border-b p-5">
          <SheetTitle className="text-lg">Your cart</SheetTitle>
          <SheetDescription>
            Select the coverages you would like to proceed with, and your broker will help you complete any
            remaining information, finalize any estimated pricing, and checkout.
          </SheetDescription>
        </SheetHeader>

        {notice && (
          <div className="px-5 pt-5">
            <Banner variant="info" small>
              {notice}
            </Banner>
          </div>
        )}

        {isEmpty ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <p className="text-ink-400 max-w-60 text-center text-sm">
              No coverages yet. Open a quote and add the coverages you want.
            </p>
          </div>
        ) : (
          <ul className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            {groups.map((group) => (
              <QuoteCartQuoteRow
                key={group.id}
                quote={group}
                onRemove={() => {
                  track(QuotesEvent.CART_ITEM_REMOVED, {
                    scope: CartItemScope.SIDEBAR,
                    item_count: group.keys.length,
                    carrier_name: group.carrierName,
                  })
                  remove(group.keys)
                }}
              />
            ))}
          </ul>
        )}

        <SheetFooter className="border-ink-100 gap-4 border-t p-5">
          {hasFailed && (
            <Banner variant="error" small>
              Something went wrong sending your selections. Try again.
            </Banner>
          )}
          {!isEmpty && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-foreground font-semibold">Estimated total</span>
              <span>
                <span className="text-foreground text-xl font-extrabold">
                  {formatCurrency(cartTotal(groups), WHOLE_DOLLARS)}
                </span>
                <span className="text-ink-500 text-sm"> / year</span>
              </span>
            </div>
          )}
          <Button
            className="w-full"
            size="lg"
            disabled={isEmpty}
            loading={isSubmitting ? 'Requesting checkout…' : undefined}
            onClick={checkOut}
          >
            Request checkout
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
